import React, { useEffect, useState } from 'react';
import {
    Panel,
    PanelType,
    mergeStyleSets,
    IPanelStyles,
} from '@fluentui/react';
import LoaderComponent from '../loader/loader.component';
import ErrorMessageComponent from '../error-message/error-message.component';
import ApiService from '../../services/api/api.service';
import useAuthStatus from '../../hooks/auth/AuthHook';
import ItemTarea from '../../models/ItemTarea';
import ItemTareaConsumo from '../../models/ItemTareaConsumo';

const panelStyles: IPanelStyles = {
    root: {}, overlay: {}, hiddenPanel: {}, main: {}, commands: { marginTop: 0 },
    contentInner: {}, scrollableContent: {},
    navigation: { backgroundColor: 'var(--color-primario)', padding: 10, alignItems: 'center' },
    header: { paddingLeft: 10, paddingRight: 10, paddingTop: 5, paddingBottom: 5 },
    headerText: { color: '#ffffff' },
    content: { padding: 0 },
    footer: {}, footerInner: {},
    subComponentStyles: {
        closeButton: {
            root: { color: '#ffffff', backgroundColor: 'transparent', padding: 0 },
            rootHovered: { backgroundColor: '#000000', color: '#ffffff', borderColor: '#000000' },
        },
    },
};

const styles = mergeStyleSets({
    sectionHeader: {
        display: 'block',
        backgroundColor: 'var(--color-primario)',
        color: 'white',
        padding: '4px 12px',
        fontWeight: 600,
        fontSize: '13px',
        marginBottom: '8px',
        marginTop: '12px',
    },
    listContainer: {
        border: '1px solid #f0f0f0',
        borderRadius: 6,
        margin: '0 12px',
        overflow: 'hidden',
    },
    listRow: {
        display: 'flex',
        alignItems: 'center',
        padding: '8px 10px',
        borderBottom: '1px solid #f0f0f0',
        gap: 8,
        selectors: {
            ':last-child': { borderBottom: 'none' },
            ':nth-child(even)': { backgroundColor: '#fafafa' },
        },
    },
    listContent: { flex: 1, minWidth: 0 },
    listDesc: { fontSize: 13, fontWeight: 600, color: '#333', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    listSub: { fontSize: 11, color: '#888' },
    listImporte: { fontSize: 13, fontWeight: 700, color: 'var(--color-primario)', minWidth: 60, textAlign: 'right' },
    emptyMsg: { padding: 16, color: '#aaa', fontSize: 13, textAlign: 'center' },
    summary: {
        padding: '8px 12px',
        fontSize: 12,
        color: '#555',
        borderTop: '2px solid #ececec',
        display: 'flex',
        justifyContent: 'space-between',
        backgroundColor: '#f8f8f8',
    },
});

interface IWorkConsumosComponentProps {
    work: ItemTarea;
    onClose: () => void;
}

const WorkConsumosComponent = (props: IWorkConsumosComponentProps) => {
    const auth = useAuthStatus();
    const [consumos, setConsumos] = useState<ItemTareaConsumo[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');

    useEffect(() => {
        if (!auth.user) return;
        setLoading(true);
        ApiService.Post<ItemTareaConsumo[]>('OrdenesTrabajo', 'GetConsumosList', auth.user, {
            IdTarea: props.work.Id,
        }).then((res: ItemTareaConsumo[]) => {
            setConsumos(res);
            setLoading(false);
        }, (err: string) => {
            setError(err);
            setLoading(false);
        });
    }, [auth.user, props.work.Id]);

    const totalImporte = consumos.reduce((s, c) => s + (c.Importe || 0), 0);

    return (
        <Panel
            isOpen={true}
            headerText="Consumos"
            onDismiss={props.onClose}
            styles={panelStyles}
            type={PanelType.medium}>

            <span className={styles.sectionHeader}>Listado de consumos</span>

            <div className={styles.listContainer}>
                {consumos.length === 0 ? (
                    <div className={styles.emptyMsg}>Sin consumos registrados.</div>
                ) : (
                    consumos.map((item, idx) => (
                        <div key={item.Id || idx} className={styles.listRow}>
                            <div className={styles.listContent}>
                                <div className={styles.listDesc}>
                                    {item.Descripcion || item.DescripcionArticulo || '(sin descripcion)'}
                                </div>
                                <div className={styles.listSub}>
                                    {item.CodArt ? item.CodArt + ' - ' : ''}
                                    Cant: {item.Cantidad} | Precio: {item.Precio?.toFixed(2)}
                                </div>
                            </div>
                            <div className={styles.listImporte}>{item.Importe?.toFixed(2)} EUR</div>
                        </div>
                    ))
                )}
            </div>

            {consumos.length > 0 && (
                <div className={styles.summary}>
                    <span>Lineas: {consumos.length}</span>
                    <span>Total: {totalImporte.toFixed(2)} EUR</span>
                </div>
            )}

            <ErrorMessageComponent message={error} />
            <LoaderComponent loading={loading} />
        </Panel>
    );
};

export default WorkConsumosComponent;
